import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase/config";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  collection, getDocs, query, where,
  doc, updateDoc, getDoc
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function FacilitatorDashboard() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [scholars, setScholars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScholar, setSelectedScholar] = useState(null);
  const [scholarData, setScholarData] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  const sessions = [
    { number: "01", title: "Lead NOW" },
    { number: "02", title: "Lead RIGHT" },
    { number: "03", title: "Lead SMART" },
    { number: "04", title: "Lead TOGETHER" },
    { number: "05", title: "Lead FORWARD" },
  ];

  async function handleLogout() {
    await signOut(auth);
    navigate("/login");
  }

  async function fetchScholars() {
    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "scholar"),
        where("status", "==", "approved")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setScholars(data);
    } catch (err) {
      console.error("Error fetching scholars:", err);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchScholars();
  }, []);

  async function openScholar(scholar) {
    setSelectedScholar(scholar);
    setActiveTab("detail");
    try {
      const docRef = doc(db, "facilitatorRecords", scholar.id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setScholarData(data.sessions || {});
        setNotes(data.notes || "");
      } else {
        setScholarData({});
        setNotes("");
      }
    } catch (err) {
      console.error("Error fetching scholar data:", err);
    }
  }

  async function toggleField(sessionNum, field) {
    const updated = {
      ...scholarData,
      [sessionNum]: {
        ...scholarData[sessionNum],
        [field]: !scholarData[sessionNum]?.[field],
      },
    };
    setScholarData(updated);
    await saveRecord(updated, notes);
  }

  async function saveRecord(sessions, notesVal) {
    if (!selectedScholar) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "facilitatorRecords", selectedScholar.id), {
        sessions,
        notes: notesVal,
        facilitatorId: currentUser.uid,
        updatedAt: new Date().toISOString(),
      });
    } catch {
      const { setDoc } = await import("firebase/firestore");
      await setDoc(doc(db, "facilitatorRecords", selectedScholar.id), {
        sessions,
        notes: notesVal,
        facilitatorId: currentUser.uid,
        updatedAt: new Date().toISOString(),
      });
    }
    setSaving(false);
  }

  async function handlePhotoUpload(sessionNum, file) {
    if (!file || !selectedScholar) return;
    setUploading({ ...uploading, [sessionNum]: true });
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `proofPhotos/${selectedScholar.id}/${sessionNum}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const updated = {
        ...scholarData,
        [sessionNum]: {
          ...scholarData[sessionNum],
          photoProof: url,
        },
      };
      setScholarData(updated);
      await saveRecord(updated, notes);
    } catch (err) {
      console.error("Upload error:", err);
    }
    setUploading({ ...uploading, [sessionNum]: false });
  }

  const completedScholars = scholars.filter(s => (s.currentSession || 1) >= 5).length;
  const inProgressScholars = scholars.filter(s => (s.currentSession || 1) > 1 && (s.currentSession || 1) < 5).length;
  const initials = `${userProfile?.firstName?.[0] || ""}${userProfile?.lastName?.[0] || ""}`;

  const getAvatarColor = (name) => {
    const colors = ["#0038A8", "#C9A84C", "#0F6E56", "#534AB7", "#993556"];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Outfit:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Outfit', sans-serif; }
        .fd-wrap { display: flex; min-height: 100vh; }
        .fd-sidebar { width: 230px; background: #060D1A; display: flex; flex-direction: column; padding: 28px 0; flex-shrink: 0; position: fixed; height: 100vh; top: 0; left: 0; overflow: hidden; z-index: 100; }
        .fd-rings { position: absolute; bottom: -80px; right: -80px; width: 300px; height: 300px; pointer-events: none; }
        .fd-ring { position: absolute; border-radius: 50%; transform: translate(-50%, -50%); top: 50%; left: 50%; }
        .fd-main { margin-left: 230px; flex: 1; background: #f5f6fa; display: flex; flex-direction: column; min-height: 100vh; }
        .fd-topbar { background: #ffffff; padding: 16px 32px; border-bottom: 1px solid #eee; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 10; }
        .fd-content { padding: 32px; flex: 1; }
        .fd-nav-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; margin: 0 12px 4px; border-radius: 10px; cursor: pointer; transition: all 0.2s; }
        .fd-nav-active { background: rgba(0,56,168,0.15); border: 1px solid rgba(0,56,168,0.25); }
        .fd-nav-item:hover { background: rgba(255,255,255,0.05); }
        .fd-stat-card { background: #ffffff; border-radius: 14px; padding: 20px 24px; border: 1px solid #f0f0f0; text-align: center; }
        .fd-card { background: #ffffff; border-radius: 14px; border: 1px solid #f0f0f0; overflow: hidden; }
        .fd-scholar-row { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #f0f0f0; cursor: pointer; transition: background 0.15s; }
        .fd-scholar-row:hover { background: #f9f9f9; }
        .fd-session-block { background: #f9f9fa; border-radius: 12px; padding: 16px; border: 1px solid #f0f0f0; margin-bottom: 10px; }
        .fd-toggle { width: 38px; height: 21px; border-radius: 999px; display: flex; align-items: center; padding: 2px; cursor: pointer; flex-shrink: 0; transition: background 0.2s; }
        .fd-toggle-on { background: #0038A8; justify-content: flex-end; }
        .fd-toggle-off { background: #e0e0e0; justify-content: flex-start; }
        .fd-toggle-dot { width: 17px; height: 17px; border-radius: 50%; background: #fff; }
        .fd-upload-area { border: 1.5px dashed #e0e0e0; border-radius: 10px; padding: 12px 16px; display: flex; align-items: center; gap: 10px; cursor: pointer; background: #fafafa; transition: border 0.2s; }
        .fd-upload-area:hover { border-color: #0038A8; }
        .fd-logout { margin: 16px 12px 0; padding: 12px; background: transparent; border: 1.5px solid rgba(255,255,255,0.15); border-radius: 10px; color: rgba(255,255,255,0.6); font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 700; cursor: pointer; text-align: center; letter-spacing: 1px; text-transform: uppercase; width: calc(100% - 24px); transition: all 0.2s; }
        .fd-logout:hover { border-color: #C9A84C; color: #C9A84C; }
        .fd-back-btn { background: transparent; border: 1.5px solid #e0e0e0; border-radius: 8px; padding: 7px 14px; font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 700; color: #666; cursor: pointer; }
        .fd-back-btn:hover { border-color: #0038A8; color: #0038A8; }
        .fd-save-btn { background: #0038A8; border: none; border-radius: 8px; padding: 8px 16px; font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 700; color: #fff; cursor: pointer; }
        .fd-mobile-header { display: none; background: #060D1A; padding: 14px 20px; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; border-bottom: 1px solid rgba(255,255,255,0.07); }
        .fd-bottom-nav { display: none; background: #060D1A; padding: 12px 0 20px; position: fixed; bottom: 0; left: 0; right: 0; z-index: 100; border-top: 1px solid rgba(255,255,255,0.07); }
        .fd-desktop-avatar { display: flex !important; }
        @media (max-width: 768px) {
          .fd-sidebar { display: none; }
          .fd-main { margin-left: 0; padding-bottom: 80px; }
          .fd-mobile-header { display: flex; }
          .fd-bottom-nav { display: block; }
          .fd-topbar { position: static; }
          .fd-content { padding: 20px; }
          .fd-stats-grid { grid-template-columns: 1fr 1fr !important; }
          .fd-desktop-avatar { display: none !important; }
        }
      `}</style>

      <div className="fd-wrap">

        {/* Sidebar */}
        <div className="fd-sidebar">
          <div className="fd-rings">
            <div className="fd-ring" style={{ width: 80, height: 80, border: "1px solid rgba(201,168,76,0.15)" }} />
            <div className="fd-ring" style={{ width: 160, height: 160, border: "1px solid rgba(201,168,76,0.1)" }} />
            <div className="fd-ring" style={{ width: 260, height: 260, border: "1px solid rgba(255,255,255,0.06)" }} />
          </div>

          <div style={{ padding: "0 20px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)", marginBottom: 20, position: "relative", zIndex: 1 }}>
            <div>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 600, color: "#fff", letterSpacing: -0.5, lineHeight: 1 }}>LEAD</span>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 600, color: "#0038A8", letterSpacing: -0.5, lineHeight: 1 }}>NOW</span>
            </div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 9, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Facilitator Portal</div>
          </div>

          <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
            <div className={`fd-nav-item ${activeTab === "overview" || activeTab === "detail" ? "fd-nav-active" : ""}`} onClick={() => setActiveTab("overview")}>
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 600, flex: 1 }}>Scholar Overview</span>
              <span style={{ color: "#C9A84C", fontSize: 8 }}>●</span>
            </div>
          </div>

          <div style={{ position: "relative", zIndex: 1 }}>
            <button className="fd-logout" onClick={handleLogout}>Sign Out</button>
          </div>
        </div>

        <div className="fd-main">

          {/* Mobile Header */}
          <div className="fd-mobile-header">
            <div>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: 20, fontWeight: 600, color: "#fff", letterSpacing: -0.5, lineHeight: 1 }}>LEAD</span>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: 20, fontWeight: 600, color: "#0038A8", letterSpacing: -0.5, lineHeight: 1 }}>NOW</span>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 8, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1, marginTop: 1 }}>Facilitator Portal</div>
            </div>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#0038A8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)" }}>
              {initials || "F"}
            </div>
          </div>

          {/* Top Bar */}
          <div className="fd-topbar">
            <div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 700, color: "#060D1A" }}>
                {activeTab === "detail" && selectedScholar ? `${selectedScholar.firstName} ${selectedScholar.lastName}` : "Facilitator Dashboard"}
              </div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#999" }}>
                Welcome, {userProfile?.firstName || "Facilitator"} · Pasig Batch 1
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {activeTab === "detail" && (
                <button className="fd-back-btn" onClick={() => setActiveTab("overview")}>← Back</button>
              )}
              {saving && <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999" }}>Saving...</span>}
              <div className="fd-desktop-avatar" style={{ width: 38, height: 38, borderRadius: "50%", background: "#0038A8", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                {initials || "F"}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="fd-content">

            {activeTab === "overview" && (
              <>
                <div className="fd-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                  <div className="fd-stat-card">
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>My Scholars</div>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 32, fontWeight: 700, color: "#060D1A" }}>{scholars.length}</div>
                  </div>
                  <div className="fd-stat-card">
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>In Progress</div>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 32, fontWeight: 700, color: "#0038A8" }}>{inProgressScholars}</div>
                  </div>
                  <div className="fd-stat-card">
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Completed</div>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 32, fontWeight: 700, color: "#C9A84C" }}>{completedScholars}</div>
                  </div>
                </div>

                <div className="fd-card">
                  <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 600, color: "#060D1A" }}>My Scholars</div>
                  </div>

                  {loading ? (
                    <div style={{ padding: 40, textAlign: "center", fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "#999" }}>Loading scholars...</div>
                  ) : scholars.length === 0 ? (
                    <div style={{ padding: 48, textAlign: "center" }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 600, color: "#060D1A", marginBottom: 6 }}>No scholars assigned yet.</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: "#999" }}>Scholars will appear here once assigned to you.</div>
                    </div>
                  ) : (
                    scholars.map((scholar, index) => {
                      const progress = Math.round(((scholar.currentSession || 1) / 5) * 100);
                      const isCompleted = (scholar.currentSession || 1) >= 5;
                      return (
                        <div key={scholar.id} className="fd-scholar-row" style={{ borderBottom: index < scholars.length - 1 ? "1px solid #f0f0f0" : "none" }} onClick={() => openScholar(scholar)}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: getAvatarColor(scholar.firstName), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                            {scholar.firstName?.[0]}{scholar.lastName?.[0]}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#060D1A" }}>{scholar.firstName} {scholar.lastName}</div>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#999" }}>{scholar.school}</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: isCompleted ? "#16a34a" : "#0038A8", fontWeight: 700, background: isCompleted ? "#dcfce7" : "rgba(0,56,168,0.08)", padding: "2px 8px", borderRadius: 999 }}>
                              {isCompleted ? "Completed" : `Session ${scholar.currentSession || 1}`}
                            </div>
                            <div style={{ background: "#f0f0f0", borderRadius: 999, height: 5, width: 60 }}>
                              <div style={{ background: "#C9A84C", borderRadius: 999, height: 5, width: `${progress}%` }} />
                            </div>
                            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: "#C9A84C", fontWeight: 700 }}>{progress}%</span>
                          </div>
                          <span style={{ color: "#ccc", fontSize: 16, marginLeft: 8 }}>›</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {activeTab === "detail" && selectedScholar && (
              <>
                <div className="fd-card" style={{ marginBottom: 16 }}>
                  <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: getAvatarColor(selectedScholar.firstName), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                      {selectedScholar.firstName?.[0]}{selectedScholar.lastName?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 16, fontWeight: 600, color: "#060D1A" }}>{selectedScholar.firstName} {selectedScholar.lastName}</div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#999" }}>{selectedScholar.school} · {selectedScholar.email}</div>
                    </div>
                    <div>
                      <div style={{ background: "#f0f0f0", borderRadius: 999, height: 6, width: 100, marginBottom: 4 }}>
                        <div style={{ background: "#C9A84C", borderRadius: 999, height: 6, width: `${Math.round(((selectedScholar.currentSession || 1) / 5) * 100)}%` }} />
                      </div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#C9A84C", fontWeight: 700, textAlign: "right" }}>{Math.round(((selectedScholar.currentSession || 1) / 5) * 100)}%</div>
                    </div>
                  </div>
                </div>

                <div className="fd-card" style={{ marginBottom: 16 }}>
                  <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#060D1A" }}>Session Records</div>
                  </div>
                  <div style={{ padding: 20 }}>
                    {sessions.map((session) => {
                      const sessionData = scholarData[session.number] || {};
                      const isOnlineSubmitted = sessionData.onlineSubmitted;
                      const isActive = parseInt(session.number) <= (selectedScholar.currentSession || 1);
                      return (
                        <div key={session.number} className="fd-session-block" style={{ opacity: isActive ? 1 : 0.4 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: "#060D1A" }}>
                              Session {session.number} · {session.title}
                            </div>
                            {isActive && (
                              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: "#0038A8", fontWeight: 700, background: "rgba(0,56,168,0.08)", padding: "2px 8px", borderRadius: 999 }}>
                                {parseInt(session.number) === (selectedScholar.currentSession || 1) ? "Active" : "Done"}
                              </div>
                            )}
                          </div>

                          <div style={{ display: "flex", gap: 20, marginBottom: 12, flexWrap: "wrap" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div className={`fd-toggle ${sessionData.attended ? "fd-toggle-on" : "fd-toggle-off"}`} onClick={() => isActive && toggleField(session.number, "attended")}>
                                <div className="fd-toggle-dot" />
                              </div>
                              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#555", fontWeight: 600 }}>Attended</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div className={`fd-toggle ${sessionData.toolCompleted ? "fd-toggle-on" : "fd-toggle-off"}`} onClick={() => isActive && toggleField(session.number, "toolCompleted")}>
                                <div className="fd-toggle-dot" />
                              </div>
                              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#555", fontWeight: 600 }}>Tool Completed</span>
                            </div>
                          </div>

                          {isOnlineSubmitted ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#dcfce7", borderRadius: 10 }}>
                              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#16a34a", fontWeight: 700 }}>Submitted Online</span>
                            </div>
                          ) : (
                            <div>
                              {sessionData.photoProof ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f0f9ff", borderRadius: 10, border: "1px solid #bae6fd" }}>
                                  <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: "#0369a1", fontWeight: 600 }}>Photo uploaded</span>
                                  <a href={sessionData.photoProof} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: "#0038A8", fontWeight: 700, marginLeft: "auto" }}>View Photo</a>
                                </div>
                              ) : (
                                <label className="fd-upload-area">
                                  <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => handlePhotoUpload(session.number, e.target.files[0])} disabled={!isActive} />
                                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(0,56,168,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <div style={{ width: 14, height: 10, border: "1.5px solid #0038A8", borderRadius: 3 }} />
                                  </div>
                                  <div>
                                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 700, color: "#060D1A" }}>
                                      {uploading[session.number] ? "Uploading..." : "Upload Photo Proof (Optional)"}
                                    </div>
                                    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, color: "#999" }}>Camera or files · JPG, PNG</div>
                                  </div>
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="fd-card">
                  <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 600, color: "#060D1A" }}>Facilitator Notes</div>
                  </div>
                  <div style={{ padding: 20 }}>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      onBlur={() => saveRecord(scholarData, notes)}
                      placeholder="Add running notes about this scholar..."
                      style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e8e8e8", background: "#f9f9f9", fontFamily: "'Outfit', sans-serif", fontSize: 13, color: "#060D1A", resize: "vertical", minHeight: 100, outline: "none" }}
                    />
                    <button className="fd-save-btn" onClick={() => saveRecord(scholarData, notes)} style={{ marginTop: 12 }}>
                      {saving ? "Saving..." : "Save Notes"}
                    </button>
                  </div>
                </div>
              </>
            )}

          </div>
        </div>

        {/* Mobile Bottom Nav */}
        <div className="fd-bottom-nav">
          <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }} onClick={() => setActiveTab("overview")}>
              <div style={{ width: 20, height: 20, borderRadius: 4, background: activeTab === "overview" ? "#0038A8" : "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(255,255,255,0.8)" }} />
              </div>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 9, color: activeTab === "overview" ? "#0038A8" : "rgba(255,255,255,0.4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Scholars</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }} onClick={handleLogout}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 10, height: 2, background: "rgba(255,255,255,0.4)", borderRadius: 1 }} />
              </div>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 9, color: "rgba(255,255,255,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Sign Out</span>
            </div>
          </div>
        </div>

      </div>
                  {/* GOSECOND Footer */}
        <div style={{
          background: "#060D1A",
          padding: "8px 24px",
          textAlign: "center",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          position: "fixed",
          bottom: 0,
          left: 230,
          right: 0,
          zIndex: 50,
        }} className="fd-footer">
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 1, textTransform: "uppercase" }}>
            © 2026 GOSECOND INC. All rights reserved.
          </span>
        </div>
    </>
  );
}
